import { useCallback, useEffect, useRef, useState } from 'react';
import { getUsers } from '../../../api';
import { User } from '../../../types';
import { isGeneralUser } from '../../../utils/isGeneralUser';
import { getApiErrorMessage } from '../../../utils/apiError';
import { sortMembersByRole } from '../membersUtils';

export function useMembersData({
  isAdmin,
  aircraftId,
  aircraftList,
  showError,
}: {
  isAdmin: boolean;
  aircraftId: number;
  aircraftList: { id: number }[];
  showError: (message: string) => void;
}) {
  const [sharesAircraftId, setSharesAircraftId] = useState<number>(0);
  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [joinedDrafts, setJoinedDrafts] = useState<Record<number, string>>({});
  const membersReqId = useRef(0);

  useEffect(() => {
    if (aircraftList.length === 0) {
      setSharesAircraftId(0);
      return;
    }
    setSharesAircraftId((prev) => {
      if (prev > 0 && aircraftList.some((a) => a.id === prev)) return prev;
      if (aircraftId > 0 && aircraftList.some((a) => a.id === aircraftId)) {
        return aircraftId;
      }
      return aircraftList[0].id;
    });
  }, [aircraftId, aircraftList]);

  const loadMembers = useCallback(
    async (forAircraftId: number = sharesAircraftId): Promise<User[]> => {
      const reqId = ++membersReqId.current;
      setLoading(true);
      try {
        const res = await getUsers(
          isAdmin,
          forAircraftId > 0 ? forAircraftId : null
        );
        if (reqId !== membersReqId.current) return [];
        const list = sortMembersByRole(res.data.filter((u) => !isGeneralUser(u)));
        setJoinedDrafts({});
        setMembers(list);
        setLoading(false);
        return list;
      } catch (error) {
        if (reqId !== membersReqId.current) return [];
        showError(getApiErrorMessage(error, 'Ошибка загрузки участников'));
        setLoading(false);
        return [];
      }
    },
    [isAdmin, sharesAircraftId, showError]
  );

  useEffect(() => {
    if (sharesAircraftId <= 0 && aircraftList.length > 0) return;
    void loadMembers(sharesAircraftId);
    return () => {
      membersReqId.current += 1;
    };
  }, [sharesAircraftId, loadMembers, aircraftList.length]);

  return {
    sharesAircraftId,
    setSharesAircraftId,
    members,
    loading,
    loadMembers,
    joinedDrafts,
    setJoinedDrafts,
  };
}
